<script setup>
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import { RouterLink, useRouter, useRoute } from 'vue-router'
import {
  Folders, Layers as LayersIcon, MessageCircle, FileText,
  LogOut, User, Settings, Users, CreditCard,
  ChevronRight, Sun, Moon, Monitor, FileSliders, Wallet, LifeBuoy, Ellipsis
} from 'lucide-vue-next'
import { useActiveTab } from '@/composables/useActiveTab'
import { useFeatures } from '@/composables/useFeatures'
import { useTheme } from '@/composables/useTheme'
import { firebase } from '@/router'
import { API, HOST } from '@/utils/http'
import { VerifiedMarker } from '@/components/ui/verified-marker'
import NotificationBell from '@/components/header/NotificationBell.vue'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

const router = useRouter()
const route = useRoute()
const store = useStore()
const { activeTab, navigateToTab, isOrgMember } = useActiveTab()
const { showBillingPage } = useFeatures()
const { themeMode, setTheme } = useTheme()

// Org control
const orgs = computed(() => store.getters['org/getOrgs'])
const selectedOrgId = computed(() => store.getters['org/getSelected'])
const selectedOrg = computed(() => store.getters['org/getSelectedOrg'])
const member = computed(() => store.getters['getMember'])

const currentOrg = computed(() => selectedOrg.value || null)
const isAdminRoute = computed(() => route.path.startsWith('/admin'))
const aiEnabled = computed(() => !!selectedOrg.value?.features?.ai_enabled)

const initials = computed(() => {
  const name = member.value?.display_name
  if (!name) return '?'
  const names = name.split(' ')
  return names[0].charAt(0) + names[names.length - 1].charAt(0)
})

// Org popover state
const isOrgOpen = ref(false)

const closeOrgPopover = () => {
  isOrgOpen.value = false
  isMobileOrgOpen.value = false
}


const goToSettings = () => {
  if (selectedOrgId.value) {
    router.push({ name: 'OrgSettings', params: { org_id: selectedOrgId.value } })
  }
  closeOrgPopover()
}

const goToMembers = () => {
  if (selectedOrgId.value) {
    router.push({ name: 'OrgMembers', params: { org_id: selectedOrgId.value } })
  }
  closeOrgPopover()
}

const goToBilling = () => {
  if (selectedOrgId.value) {
    router.push({ name: 'OrgBilling', params: { org_id: selectedOrgId.value } })
  }
  closeOrgPopover()
}

// User popover state
const isUserOpen = ref(false)
const showThemeMenu = ref(false)

const logOutUser = () => {
  store.dispatch('logout')
}

// Zendesk
const zendeskAvailable = ref(false)

const getZendeskSsoUrl = async () => {
  try {
    const firebaseToken = await firebase.auth.currentUser.getIdToken(true)
    const response = await API({
      method: 'POST',
      url: `${HOST}/zendesk-auth`,
      data: { firebase_token: firebaseToken }
    })
    return response.data || null
  } catch (error) {
    console.error('Error fetching Zendesk SSO URL:', error)
    return null
  }
}

const handleZendeskLogin = async () => {
  try {
    const zendeskData = await getZendeskSsoUrl()
    if (zendeskData && zendeskData.url && zendeskData.jwt) {
      const form = document.createElement('form')
      form.method = 'POST'
      form.action = zendeskData.url
      form.target = '_blank'

      const jwtInput = document.createElement('input')
      jwtInput.type = 'hidden'
      jwtInput.name = 'jwt'
      jwtInput.value = zendeskData.jwt

      form.appendChild(jwtInput)
      document.body.appendChild(form)
      form.submit()
      document.body.removeChild(form)
    }
  } catch (error) {
    console.error('Error opening Zendesk Help Centre:', error)
  }
}

// Unread chat messages across all conversations — shown as a badge on the tab.
const chatUnread = computed(() => store.getters['chat/totalUnread'])

// Nav items
const navItems = computed(() => {
  const items = [
    { id: 'projects', label: 'Projects', icon: Folders, show: true },
    { id: 'layers', label: 'Maps', icon: LayersIcon, show: isOrgMember.value },
    // Chat is member-to-member messaging, so every org member gets it; only the
    // agent affordances inside are gated on ai_enabled.
    { id: 'chats', label: 'Chats', icon: MessageCircle, show: isOrgMember.value, badge: chatUnread.value },
    { id: 'reports', label: 'Reports', icon: FileText, show: isOrgMember.value, disabled: !aiEnabled.value },
    { id: 'payments', label: 'Payments', icon: Wallet, show: isOrgMember.value },
  ]
  return items.filter(item => item.show)
})

// Mobile nav state
const isMobileOrgOpen = ref(false)
const isMobileUserOpen = ref(false)
const isOverflowOpen = ref(false)
const windowWidth = ref(window.innerWidth)

const handleResize = () => { windowWidth.value = window.innerWidth }

const MOBILE_ITEM_WIDTH = 48
const MOBILE_PERMANENT_COUNT = 3 // org, bell, user

const maxVisibleNavItems = computed(() => {
  const maxSlots = Math.floor(windowWidth.value / MOBILE_ITEM_WIDTH)
  const available = maxSlots - MOBILE_PERMANENT_COUNT
  if (available >= navItems.value.length) return navItems.value.length
  return Math.max(0, available - 1) // -1 for overflow button
})

const visibleNavItems = computed(() => navItems.value.slice(0, maxVisibleNavItems.value))
const overflowNavItems = computed(() => navItems.value.slice(maxVisibleNavItems.value))

onMounted(async () => {
  const isLoggedIn = store.getters['getIsLoggedIn']
  if (isLoggedIn) {
    store.dispatch('org/resolveSelectedOrg')
    const zendeskData = await getZendeskSsoUrl()
    zendeskAvailable.value = !!(zendeskData && zendeskData.url && zendeskData.jwt)
  }
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

watch(() => orgs.value, (newOrgs) => {
  if (newOrgs.length > 0 && !selectedOrgId.value) {
    store.dispatch('org/resolveSelectedOrg')
  }
})
</script>

<template>
  <TooltipProvider :delay-duration="300">
    <div class="contents">
    <nav class="hidden sm:flex flex-col w-[74px] h-full bg-background border-r border-border flex-shrink-0">
      <!-- Top: Org selector -->
      <div class="flex items-center justify-center py-2">
        <Tooltip v-bind="isOrgOpen ? { open: false } : {}">
          <Popover v-model:open="isOrgOpen">
            <TooltipTrigger as-child>
              <PopoverTrigger as-child>
                <Button variant="ghost" size="nav" class="text-muted-foreground">
                  <Avatar class="bg-transparent">
                    <AvatarImage
                      v-if="currentOrg && currentOrg.avatar_url"
                      :src="currentOrg.avatar_url"
                      :alt="currentOrg.organization_name"
                    />
                    <img v-else src="@/assets/img/main-logo.svg" alt="logo" class="w-10 h-10" />
                  </Avatar>
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="right" :side-offset="8">
              {{ currentOrg?.organization_name || 'Organization' }}
            </TooltipContent>
          <PopoverContent class="w-64 p-0" side="right" align="start" :side-offset="8">
            <div class="p-3 border-b">
              <p class="text-sm font-medium truncate">{{ currentOrg?.organization_name }}</p>
            </div>
            <div class="p-0.5">
              <Button v-if="isOrgMember" @click="goToSettings" variant="ghost" class="w-full justify-start">
                <Settings class="w-4 h-4 mr-3" />
                Settings
              </Button>
              <Button v-if="isOrgMember" @click="goToMembers" variant="ghost" class="w-full justify-start">
                <Users class="w-4 h-4 mr-3" />
                Members
              </Button>
              <Button v-if="isOrgMember && showBillingPage" @click="goToBilling" variant="ghost" class="w-full justify-start">
                <CreditCard class="w-4 h-4 mr-3" />
                Billing
              </Button>
              <Button :as="RouterLink" :to="{ name: 'AuthOverview' }" @click="closeOrgPopover" variant="ghost" class="w-full flex justify-between">
                Switch Organization
                <ChevronRight class="w-4.5 h-4.5" />
              </Button>
            </div>
            </PopoverContent>
          </Popover>
        </Tooltip>
      </div>

      <!-- Middle: Main nav -->
      <div class="flex-1 flex flex-col items-center gap-0.5 pb-2">
        <Tooltip v-for="item in navItems" :key="item.id">
          <TooltipTrigger as-child>
            <Button
              variant="ghost"
              size="nav"
              @click="item.disabled ? null : navigateToTab(item.id)"
              class="flex-col"
              :class="[
                activeTab === item.id && !isAdminRoute ? 'bg-accent text-primary' : 'text-muted-foreground',
                item.disabled ? 'opacity-40 cursor-not-allowed' : ''
              ]"
              :data-testid="'nav-' + item.id"
            >
              <span class="relative">
                <component :is="item.icon" class="w-6 h-6" />
                <span
                  v-if="item.badge"
                  class="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-white"
                >
                  {{ item.badge > 99 ? '99+' : item.badge }}
                </span>
              </span>
              <span class="text-[11px] font-bold mt-0.5 leading-tight">{{ item.label }}</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right" :side-offset="8">
            {{ item.disabled ? 'Contact sales' : item.label }}
          </TooltipContent>
        </Tooltip>
      </div>

      <!-- Bottom: Utilities -->
      <div class="mt-auto flex flex-col items-center gap-0.5 pb-2">
          <NotificationBell popover-side="right" />

        <!-- User menu -->
        <Tooltip v-bind="isUserOpen ? { open: false } : {}">
          <Popover v-model:open="isUserOpen">
            <TooltipTrigger as-child>
              <PopoverTrigger as-child>
                <Button variant="ghost" size="nav" class="text-muted-foreground" data-testid="user-menu-trigger">
                  <Avatar class="w-9 h-9">
                    <AvatarImage :src="member?.avatar_url" :alt="member?.display_name" />
                    <AvatarFallback class="text-xs">{{ initials }}</AvatarFallback>
                  </Avatar>
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="right" :side-offset="8">
              Account
            </TooltipContent>
          <PopoverContent class="w-56 p-0" side="right" align="end" :side-offset="8">
            <div class="p-3 border-b">
              <p class="text-sm font-bold flex items-center gap-1">
                <VerifiedMarker v-if="member?.verified" />
                {{ member?.display_name }}
              </p>
              <p v-if="member?.email" class="text-xs text-muted-foreground">{{ member.email }}</p>
              <p v-else-if="member?.phone" class="text-xs text-muted-foreground">{{ member.phone }}</p>
            </div>
            <div class="p-0.5">
              <Button @click="router.push('/user'); isUserOpen = false" variant="ghost" class="w-full justify-start">
                <User class="w-4 h-4 mr-3" />
                Profile
              </Button>

              <!-- Theme submenu -->
              <div class="relative">
                <Button @click="showThemeMenu = !showThemeMenu" variant="ghost" class="w-full flex justify-between">
                  <span class="flex items-center">
                    <Sun class="w-4 h-4 mr-3 dark:hidden" />
                    <Moon class="w-4 h-4 mr-3 hidden dark:block" />
                    Theme
                  </span>
                  <ChevronRight class="w-4 h-4" :class="{ 'rotate-90': showThemeMenu }" />
                </Button>
                <div v-if="showThemeMenu" class="px-1 pb-1">
                  <Button
                    v-for="t in [{ value: 'light', label: 'Light', icon: Sun }, { value: 'dark', label: 'Dark', icon: Moon }, { value: 'system', label: 'System', icon: Monitor }]"
                    :key="t.value"
                    @click="setTheme(t.value); showThemeMenu = false"
                    variant="ghost"
                    class="w-full justify-start"
                    :class="themeMode === t.value ? 'bg-accent' : ''"
                  >
                    <component :is="t.icon" class="w-4 h-4 mr-3" />
                    {{ t.label }}
                  </Button>
                </div>
              </div>

              <Separator v-if="zendeskAvailable" />
              <Button v-if="zendeskAvailable" @click="handleZendeskLogin(); isUserOpen = false" variant="ghost" class="w-full justify-start">
                <LifeBuoy class="w-4 h-4 mr-3" />
                Help Centre
              </Button>

              <template v-if="member?.is_super_user">
                <Separator />
                <Button @click="router.push('/admin/payments'); isUserOpen = false" variant="ghost" class="w-full justify-start">
                  <Wallet class="w-4 h-4 mr-3" />
                  <span class="flex-1 text-left">Payments</span>
                  <span class="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">Admin</span>
                </Button>
                <Button @click="router.push('/admin'); isUserOpen = false" variant="ghost" class="w-full justify-start">
                  <FileSliders class="w-4 h-4 mr-3" />
                  <span class="flex-1 text-left">Tools</span>
                  <span class="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">Admin</span>
                </Button>
              </template>

              <Separator />
              <Button @click="logOutUser" variant="ghost" class="w-full justify-start">
                <LogOut class="w-4 h-4 mr-3" />
                Log out
              </Button>
            </div>
            </PopoverContent>
          </Popover>
        </Tooltip>
      </div>

    </nav>

    <!-- Mobile bottom nav -->
    <nav class="mobile-nav flex sm:hidden items-center fixed bottom-0 left-0 right-0 h-14 bg-background border-t border-border z-50 px-1">
      <!-- Left: Org selector -->
      <div class="flex-shrink-0">
        <Popover v-model:open="isMobileOrgOpen">
          <PopoverTrigger as-child>
            <Button variant="ghost" size="nav-sm" class="text-muted-foreground">
              <Avatar class="bg-transparent">
                <AvatarImage
                  v-if="currentOrg && currentOrg.avatar_url"
                  :src="currentOrg.avatar_url"
                  :alt="currentOrg.organization_name"
                />
                <img v-else src="@/assets/img/main-logo.svg" alt="logo" class="w-9 h-9" />
              </Avatar>
            </Button>
          </PopoverTrigger>
          <PopoverContent class="w-64 p-0" side="top" align="start" :side-offset="8">
            <div class="p-3 border-b">
              <p class="text-sm font-medium truncate">{{ currentOrg?.organization_name }}</p>
            </div>
            <div class="p-0.5">
              <Button v-if="isOrgMember" @click="goToSettings" variant="ghost" class="w-full justify-start">
                <Settings class="w-4 h-4 mr-3" />
                Settings
              </Button>
              <Button v-if="isOrgMember" @click="goToMembers" variant="ghost" class="w-full justify-start">
                <Users class="w-4 h-4 mr-3" />
                Members
              </Button>
              <Button v-if="isOrgMember && showBillingPage" @click="goToBilling" variant="ghost" class="w-full justify-start">
                <CreditCard class="w-4 h-4 mr-3" />
                Billing
              </Button>
              <Button :as="RouterLink" :to="{ name: 'AuthOverview' }" @click="closeOrgPopover" variant="ghost" class="w-full flex justify-between">
                Switch Organization
                <ChevronRight class="w-4.5 h-4.5" />
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <!-- Center: Main nav -->
      <div class="flex-1 flex items-center justify-center gap-0.5">
        <Button
          v-for="item in visibleNavItems"
          :key="item.id"
          variant="ghost"
          size="nav-sm"
          @click="item.disabled ? null : navigateToTab(item.id)"
          class="flex-col"
          :class="[
            activeTab === item.id ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
            item.disabled ? 'opacity-40 cursor-not-allowed' : ''
          ]"
        >
          <span class="relative">
            <component :is="item.icon" class="w-5 h-5" />
            <span
              v-if="item.badge"
              class="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-white"
            >
              {{ item.badge > 99 ? '99+' : item.badge }}
            </span>
          </span>
          <span class="text-[10px] mt-0.5 leading-tight">{{ item.label }}</span>
        </Button>

        <!-- Overflow menu -->
        <Popover v-if="overflowNavItems.length > 0" v-model:open="isOverflowOpen">
          <PopoverTrigger as-child>
            <Button variant="ghost" size="nav-sm" class="flex-col text-muted-foreground">
              <Ellipsis class="w-5 h-5" />
              <span class="text-[10px] mt-0.5 leading-tight">More</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent class="w-48 p-0.5" side="top" :side-offset="8">
            <Button
              v-for="item in overflowNavItems"
              :key="item.id"
              variant="ghost"
              class="w-full justify-start"
              :class="[
                activeTab === item.id ? 'bg-accent' : '',
                item.disabled ? 'opacity-40 cursor-not-allowed' : ''
              ]"
              @click="item.disabled ? null : (navigateToTab(item.id), isOverflowOpen = false)"
            >
              <component :is="item.icon" class="w-4 h-4 mr-3" />
              {{ item.disabled ? 'Contact sales' : item.label }}
              <span
                v-if="item.badge"
                class="ml-auto flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-white"
              >
                {{ item.badge > 99 ? '99+' : item.badge }}
              </span>
            </Button>
          </PopoverContent>
        </Popover>
      </div>

      <!-- Right: Notifications + User -->
      <div class="flex-shrink-0 flex items-center">
        <NotificationBell popover-side="top" tooltip-side="top" button-size="nav-sm" />

        <Popover v-model:open="isMobileUserOpen">
        <PopoverTrigger as-child>
          <Button variant="ghost" size="nav-sm" class="text-muted-foreground">
            <Avatar class="w-9 h-9">
              <AvatarImage :src="member?.avatar_url" :alt="member?.display_name" />
              <AvatarFallback class="text-xs">{{ initials }}</AvatarFallback>
            </Avatar>
          </Button>
        </PopoverTrigger>
        <PopoverContent class="w-56 p-0" side="top" align="end" :side-offset="8">
          <div class="p-3 border-b">
            <p class="text-sm font-bold flex items-center gap-1">
              <VerifiedMarker v-if="member?.verified" />
              {{ member?.display_name }}
            </p>
            <p v-if="member?.email" class="text-xs text-muted-foreground">{{ member.email }}</p>
            <p v-else-if="member?.phone" class="text-xs text-muted-foreground">{{ member.phone }}</p>
          </div>
          <div class="p-0.5">
            <Button @click="router.push('/user'); isMobileUserOpen = false" variant="ghost" class="w-full justify-start">
              <User class="w-4 h-4 mr-3" />
              Profile
            </Button>

            <!-- Theme submenu -->
            <div class="relative">
              <Button @click="showThemeMenu = !showThemeMenu" variant="ghost" class="w-full flex justify-between">
                <span class="flex items-center">
                  <Sun class="w-4 h-4 mr-3 dark:hidden" />
                  <Moon class="w-4 h-4 mr-3 hidden dark:block" />
                  Theme
                </span>
                <ChevronRight class="w-4 h-4" :class="{ 'rotate-90': showThemeMenu }" />
              </Button>
              <div v-if="showThemeMenu" class="px-1 pb-1">
                <Button
                  v-for="t in [{ value: 'light', label: 'Light', icon: Sun }, { value: 'dark', label: 'Dark', icon: Moon }, { value: 'system', label: 'System', icon: Monitor }]"
                  :key="t.value"
                  @click="setTheme(t.value); showThemeMenu = false"
                  variant="ghost"
                  class="w-full justify-start"
                  :class="themeMode === t.value ? 'bg-accent' : ''"
                >
                  <component :is="t.icon" class="w-4 h-4 mr-3" />
                  {{ t.label }}
                </Button>
              </div>
            </div>

            <Separator v-if="zendeskAvailable" />
            <Button v-if="zendeskAvailable" @click="handleZendeskLogin(); isMobileUserOpen = false" variant="ghost" class="w-full justify-start">
              <LifeBuoy class="w-4 h-4 mr-3" />
              Help Centre
            </Button>

            <template v-if="member?.is_super_user">
              <Separator />
              <Button @click="router.push('/admin/payments'); isMobileUserOpen = false" variant="ghost" class="w-full justify-start">
                <Wallet class="w-4 h-4 mr-3" />
                <span class="flex-1 text-left">Payments</span>
                <span class="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">Admin</span>
              </Button>
              <Button @click="router.push('/admin'); isMobileUserOpen = false" variant="ghost" class="w-full justify-start">
                <FileSliders class="w-4 h-4 mr-3" />
                <span class="flex-1 text-left">Tools</span>
                <span class="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">Admin</span>
              </Button>
            </template>

            <Separator />
            <Button @click="logOutUser" variant="ghost" class="w-full justify-start">
              <LogOut class="w-4 h-4 mr-3" />
              Log out
            </Button>
          </div>
        </PopoverContent>
        </Popover>
      </div>
    </nav>
    </div>
  </TooltipProvider>
</template>

<style scoped>
.mobile-nav {
  padding-bottom: env(safe-area-inset-bottom);
}
</style>

